import { Link } from 'react-router-dom'
import { humanizeEnum, formatNumber } from '@/lib/format'
import { toneForStatus } from '@/components/ui/status-tone'
import { EmptyState } from '@/components/ui/States'
import styles from './charts.module.css'

interface StatusBreakdownProps {
  counts: Record<string, number>
  /** Filtered list to open when a row is clicked. */
  linkTo: (status: string) => string
  emptyMessage: string
}

/**
 * Status distribution as a labelled bar list rather than a pie.
 *
 * Every slice carries its exact count, and each row links to the same list
 * filtered by that status — the number is a way into the work, not just a
 * decoration.
 */
export function StatusBreakdown({ counts, linkTo, emptyMessage }: StatusBreakdownProps) {
  const entries = Object.entries(counts)
    .filter(([, value]) => value > 0)
    .sort(([, a], [, b]) => b - a)

  if (entries.length === 0) {
    return <EmptyState title="Nothing recorded yet" description={emptyMessage} compact />
  }

  const total = entries.reduce((sum, [, value]) => sum + value, 0)

  return (
    <ul className={styles.breakdownList} role="list">
      {entries.map(([status, count]) => {
        const share = total === 0 ? 0 : Math.round((count / total) * 100)
        return (
          <li key={status}>
            <Link to={linkTo(status)} className={styles.breakdownRow}>
              <span className={styles.breakdownHeader}>
                <span className={styles.breakdownName} data-tone={toneForStatus(status)}>
                  {humanizeEnum(status)}
                </span>
                <span className={styles.breakdownCount}>
                  {formatNumber(count)}
                  <span className={styles.breakdownShare}>{share}%</span>
                </span>
              </span>
              <span className={styles.breakdownTrack}>
                <span
                  className={styles.breakdownFill}
                  data-tone={toneForStatus(status)}
                  style={{ width: `${Math.max(share, 2)}%` }}
                />
              </span>
            </Link>
          </li>
        )
      })}
    </ul>
  )
}
